// AI-search / GEO (Generative Engine Optimisation) content + schema helpers
// All factual, no fabricated reviews or addresses.

import { BIZ } from "@/lib/content";

// ── Person / Author schema ────────────────────────────────────────────────
// Ryan Fuller — Founder & Director of FCA Reports Australia.
// Listed honestly: Physiotherapist and Rehabilitation Consultant. Not a doctor.
// He is the PRIMARY reviewed-by / content authority entity for the site.
export const RYAN_FULLER_SCHEMA = {
  "@type": "Person",
  "@id": `${BIZ.url}/#ryan-fuller`,
  "name": "Ryan Fuller",
  "givenName": "Ryan",
  "familyName": "Fuller",
  "jobTitle": "Founder & Director",
  "honorificSuffix": "BHSc, MPhty",
  "description":
    "Australian Physiotherapist and Rehabilitation Consultant. Founder of FCA Reports Australia, an independent NDIS functional assessment service delivering reports nationally via telehealth. Clinical experience spans intensive care, orthopaedics, neurological rehabilitation, cardiorespiratory care, private practice and NDIS Functional Capacity Assessment.",
  "alumniOf": [
    {
      "@type": "CollegeOrUniversity",
      "name": "La Trobe University",
      "sameAs": "https://www.latrobe.edu.au/",
    },
  ],
  "hasCredential": [
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "degree",
      "name": "Bachelor of Health Science",
      "educationalLevel": "Bachelor",
      "recognizedBy": { "@type": "CollegeOrUniversity", "name": "La Trobe University" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "degree",
      "name": "Master of Physiotherapy",
      "educationalLevel": "Master",
      "recognizedBy": { "@type": "CollegeOrUniversity", "name": "La Trobe University" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "license",
      "name": "AHPRA Registered Physiotherapist",
      "recognizedBy": {
        "@type": "Organization",
        "name": "Australian Health Practitioner Regulation Agency (AHPRA)",
        "sameAs": "https://www.ahpra.gov.au/",
      },
    },
  ],
  "hasOccupation": [
    {
      "@type": "Occupation",
      "name": "Physiotherapist",
      "occupationalCategory": "Physiotherapist (ANZSCO 252511)",
      "skills": [
        "Functional Capacity Assessment",
        "Neurological rehabilitation",
        "Orthopaedic rehabilitation",
        "Cardiorespiratory physiotherapy",
        "Intensive care unit (ICU) physiotherapy",
        "Hospital inpatient and outpatient care",
        "Private practice physiotherapy",
      ],
    },
    {
      "@type": "Occupation",
      "name": "Rehabilitation Consultant",
      "skills": [
        "NDIS Functional Capacity Assessment",
        "Supported Independent Living (SIL) assessment",
        "Specialist Disability Accommodation (SDA) assessment",
        "Return-to-work assessment",
        "Independent medical assessment review",
      ],
    },
  ],
  "knowsAbout": [
    "NDIS Functional Capacity Assessment",
    "Supported Independent Living Assessment",
    "Specialist Disability Accommodation Assessment",
    "Disability Rehabilitation",
    "Neurological Physiotherapy",
    "Orthopaedic Physiotherapy",
    "Cardiorespiratory Physiotherapy",
    "Intensive Care Physiotherapy",
    "Private Practice Physiotherapy",
    "Telehealth Allied Health",
  ],
  "memberOf": [
    {
      "@type": "Organization",
      "name": "AHPRA — Australian Health Practitioner Regulation Agency",
      "sameAs": "https://www.ahpra.gov.au/",
    },
  ],
  "worksFor": { "@id": `${BIZ.url}/#organization` },
  "url": `${BIZ.url}/about`,
  // ── Identity-verification URLs (sameAs) ───────────────────────────────
  // Update these once Ryan's public profile URLs are confirmed. Including
  // a single LinkedIn URL is the single highest-impact E-E-A-T signal for
  // Google's Knowledge Graph. AHPRA Practitioner Register URL is also
  // powerful — find via https://www.ahpra.gov.au/Registration/Registers-of-Practitioners.aspx
  // Leave empty until verified. DO NOT fabricate.
  "sameAs": [
    // "https://www.linkedin.com/in/ryan-fuller-physiotherapist/",
    // "https://www.ahpra.gov.au/Registration/Registers-of-Practitioners.aspx?...",
  ],
};
// Amy-Lynne Simmons — Clinical Director and Physiotherapist at FCA Reports
// Australia. Australian Physiotherapist (Doctor of Physiotherapy, Bond
// University; Bachelor of Exercise and Sport Science, University of
// Tasmania). Strengthens authority as a second peer-reviewing clinician
// without displacing Ryan as the primary author/reviewer entity.
// No fabricated awards, memberships, publications, or addresses.
export const AMY_LYNNE_SIMMONS_SCHEMA = {
  "@type": "Person",
  "@id": `${BIZ.url}/#amy-lynne-simmons`,
  "name": "Amy-Lynne Simmons",
  "givenName": "Amy-Lynne",
  "familyName": "Simmons",
  "jobTitle": "Clinical Director",
  "honorificSuffix": "BExSpSci, DPT",
  "description":
    "Clinical Director of FCA Reports Australia and an AHPRA-registered Physiotherapist. Leads clinical governance and peer review across the practice's NDIS Functional Capacity Assessments, working alongside Ryan Fuller.",
  "alumniOf": [
    {
      "@type": "CollegeOrUniversity",
      "name": "Bond University",
      "sameAs": "https://bond.edu.au/",
    },
    {
      "@type": "CollegeOrUniversity",
      "name": "University of Tasmania",
      "sameAs": "https://www.utas.edu.au/",
    },
  ],
  "hasCredential": [
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "degree",
      "name": "Doctor of Physiotherapy",
      "educationalLevel": "Professional doctorate",
      "recognizedBy": { "@type": "CollegeOrUniversity", "name": "Bond University" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "degree",
      "name": "Bachelor of Exercise and Sport Science",
      "educationalLevel": "Bachelor",
      "recognizedBy": { "@type": "CollegeOrUniversity", "name": "University of Tasmania" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "license",
      "name": "AHPRA Registered Physiotherapist",
      "recognizedBy": {
        "@type": "Organization",
        "name": "Australian Health Practitioner Regulation Agency (AHPRA)",
        "sameAs": "https://www.ahpra.gov.au/",
      },
    },
  ],
  "hasOccupation": [
    {
      "@type": "Occupation",
      "name": "Physiotherapist",
      "occupationalCategory": "Physiotherapist (ANZSCO 252511)",
      "skills": [
        "Functional Capacity Assessment",
        "NDIS assessment",
        "Telehealth Functional Capacity Assessment",
        "Exercise and Sport Science",
      ],
    },
  ],
  "knowsAbout": [
    "NDIS Functional Capacity Assessment",
    "Telehealth Functional Capacity Assessment",
    "Physiotherapy",
    "Exercise and Sport Science",
    "Supported Independent Living Assessment",
    "Specialist Disability Accommodation Assessment",
    "NDIS",
  ],
  "memberOf": [
    {
      "@type": "Organization",
      "name": "AHPRA — Australian Health Practitioner Regulation Agency",
      "sameAs": "https://www.ahpra.gov.au/",
    },
  ],
  "worksFor": { "@id": `${BIZ.url}/#organization` },
  "url": `${BIZ.url}/about`,
  // ── Identity-verification URLs (sameAs) ───────────────────────────────
  // Add Amy-Lynne's verified public profile URLs here. Same E-E-A-T
  // principle as Ryan: a single LinkedIn URL meaningfully strengthens
  // Google's knowledge-graph link to the person entity.
  // Leave empty until verified. DO NOT fabricate.
  "sameAs": [
    // "https://www.linkedin.com/in/amy-lynne-simmons/",
    // "https://www.ahpra.gov.au/Registration/Registers-of-Practitioners.aspx?...",
  ],
};

// ── Organisation schema ────────────────────────────────────────────────────
// Ryan Fuller is referenced via `founder` (primary authority).
// Amy-Lynne Simmons appears via `member` + `employee` (secondary authority,
// strengthens multidisciplinary trust without displacing Ryan as the lead author).
export const ORGANIZATION_SCHEMA = {
  "@type": "Organization",
  "@id": `${BIZ.url}/#organization`,
  "name": BIZ.name,
  "legalName": BIZ.legal,
  "url": BIZ.url,
  "logo": `${BIZ.url}/icon-512.png`,
  "image": `${BIZ.url}/og-image.png`,
  "telephone": BIZ.phone,
  "email": BIZ.email,
  "founder": { "@id": `${BIZ.url}/#ryan-fuller` },
  "founders": [{ "@id": `${BIZ.url}/#ryan-fuller` }],
  "employee": [
    { "@id": `${BIZ.url}/#ryan-fuller` },
    { "@id": `${BIZ.url}/#amy-lynne-simmons` },
  ],
  "member": [
    { "@id": `${BIZ.url}/#ryan-fuller` },
    { "@id": `${BIZ.url}/#amy-lynne-simmons` },
  ],
  "areaServed": { "@type": "Country", "name": "Australia" },
  "knowsAbout": [
    "Functional Capacity Assessment",
    "NDIS",
    "Physiotherapy",
    "Telehealth Physiotherapy",
    "Rehabilitation",
    "Supported Independent Living",
    "Specialist Disability Accommodation",
    "Allied Health Telehealth",
  ],
};

// ── MedicalBusiness with services list ─────────────────────────────────────
export const MEDICAL_BUSINESS_SCHEMA = {
  "@type": "MedicalBusiness",
  "@id": `${BIZ.url}/#medicalbusiness`,
  "name": BIZ.name,
  "url": BIZ.url,
  "telephone": BIZ.phone,
  "medicalSpecialty": ["PhysicalTherapy"],
  "availableService": [
    { "@type": "MedicalProcedure", "name": "NDIS Functional Capacity Assessment", "url": `${BIZ.url}/services/functional-capacity-assessment` },
    { "@type": "MedicalProcedure", "name": "Supported Independent Living (SIL) Assessment", "url": `${BIZ.url}/services/sil-assessments` },
    { "@type": "MedicalProcedure", "name": "Specialist Disability Accommodation (SDA) Assessment", "url": `${BIZ.url}/services/sda-assessments` },
  ],
  "areaServed": { "@type": "Country", "name": "Australia" },
};

// ── Reusable Service schema factory ────────────────────────────────────────
// Service nodes now reference Ryan Fuller via `reviewedBy` and the Organization
// via `provider`. This is the highest-impact E-E-A-T property for clinical
// service pages — it tells Google + AI engines who clinically signed off.
export function buildServiceSchema({ name, description, slug, serviceType = "Functional Capacity Assessment", city = null }) {
  const url = `${BIZ.url}/services/${slug}`;
  const area = city
    ? { "@type": "City", "name": city.name, "containedInPlace": { "@type": "State", "name": city.state } }
    : { "@type": "Country", "name": "Australia" };
  return {
    "@type": "Service",
    "@id": `${url}#service`,
    "name": name,
    "description": description,
    "serviceType": serviceType,
    "provider": { "@id": `${BIZ.url}/#organization` },
    "reviewedBy": { "@id": `${BIZ.url}/#ryan-fuller` },
    "areaServed": area,
    "url": url,
    "audience": { "@type": "PeopleAudience", "audienceType": "NDIS Participants, Support Coordinators, Plan Managers" },
  };
}

// ── Reusable FAQPage schema factory ────────────────────────────────────────
export function buildFAQSchema(faqs) {
  return {
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
}

// ── AI-search-friendly Q&A content (natural questions people ask Google/ChatGPT)
// These answers are intentionally complete sentences so AI snippets cite them well.
export const AI_FAQS_GENERIC = [
  {
    q: "What is a Functional Capacity Assessment?",
    a: "A Functional Capacity Assessment (FCA) is a structured clinical evaluation that documents how a person's disability affects their daily functioning, supervision needs, community participation and safety. For NDIS purposes, a Functional Capacity Assessment report translates a participant's diagnosis into the practical evidence the NDIA uses to determine reasonable and necessary funding.",
  },
  {
    q: "Who needs an NDIS Functional Capacity Assessment?",
    a: "NDIS participants typically need a Functional Capacity Assessment when applying for new funding, requesting a plan reassessment, transitioning to higher-support services like SIL or SDA, or providing evidence at an internal review or Administrative Review Tribunal (ART) appeal. Support Coordinators and Plan Managers often request an FCA to strengthen a participant's plan-build evidence base.",
  },
  {
    q: "Can FCA Reports Australia complete assessments virtually?",
    a: "Yes. FCA Reports Australia delivers Functional Capacity Assessments 100% virtually via secure telehealth, covering all Australian states and territories. Participants can be assessed from home, with parents, carers, support workers or Support Coordinators joining the same secure session where appropriate.",
  },
  {
    q: "Are reports completed by Occupational Therapists?",
    a: "FCA Reports Australia delivers Functional Capacity Assessments through an AHPRA-registered Occupational Therapy team with multidisciplinary clinical oversight from our Director team. NDIS guidance recognises Occupational Therapists as suitably qualified FCA assessors; our practice focuses on Occupational Therapy-led reports, with deep clinical depth across cognitive, sensory, psychosocial, motor and complex functional presentations.",
  },
  {
    q: "How long does an FCA report take?",
    a: "Standard NDIS Functional Capacity Assessment reports are issued within five business days of the final assessment interview. The full process — from initial referral to issued PDF — typically completes inside two weeks, depending on document availability and participant scheduling.",
  },
  {
    q: "Can Support Coordinators refer participants?",
    a: "Yes. Support Coordinators are encouraged to refer NDIS participants directly through the website or by emailing the secure referral inbox. A dedicated coordinator pathway is offered, including weekly status updates and direct access to the assessing clinician on request.",
  },
];

export const AI_FAQS_SIL = [
  {
    q: "What is a SIL Assessment?",
    a: "A Supported Independent Living (SIL) Assessment is the independent clinical evidence that supports a participant's NDIS application for shared 24/7 disability supports in the home. The SIL Assessment report documents support tasks, recommended ratio, Roster of Care and behaviour overlay.",
  },
  {
    q: "Why use an independent SIL assessor instead of the chosen SIL provider?",
    a: "The NDIA increasingly scrutinises SIL Assessments completed by the same organisation that will provide the funded supports. An independent SIL assessor — with no commercial interest in the outcome — adds credibility to the recommended Roster of Care and support ratio.",
  },
  {
    q: "Does FCA Reports Australia complete SIL Assessments Australia-wide?",
    a: "Yes. SIL Assessments are delivered virtually across all Australian states and territories by AHPRA-registered Occupational Therapists, with reports typically issued within five business days of the final assessment interview.",
  },
];

export const AI_FAQS_SDA = [
  {
    q: "What is an SDA Assessment?",
    a: "A Specialist Disability Accommodation (SDA) Assessment is the housing-evidence report that supports a participant's NDIS application for purpose-built or modified housing. It documents physical access requirements, design category eligibility and the clinical rationale for SDA funding.",
  },
  {
    q: "Who can complete an SDA Assessment?",
    a: "SDA Assessments are completed by suitably qualified allied health professionals with experience in housing modifications. FCA Reports Australia provides independent SDA Assessment reports written specifically to the SDA Operational Guidelines by our AHPRA-registered Occupational Therapy team.",
  },
  {
    q: "How long does an SDA Assessment take?",
    a: "An SDA Assessment is usually completed inside two to three weeks from initial referral, with the written report issued within five business days of the final assessment interview. Complex high-physical-support cases may take slightly longer where extensive housing inspections are required.",
  },
];

export const AI_FAQS_LOCATION = (city) => [
  {
    q: `Who provides Functional Capacity Assessments in ${city}?`,
    a: `FCA Reports Australia provides independent NDIS Functional Capacity Assessments to participants based in ${city} and surrounding suburbs. Reports are written by AHPRA-registered Occupational Therapists with experience in disability rehabilitation.`,
  },
  {
    q: `Can Functional Capacity Assessments be completed virtually in ${city}?`,
    a: `Yes. All Functional Capacity Assessments for ${city} participants are delivered 100% virtually via secure telehealth. This makes the service accessible to participants across metropolitan ${city} and regional areas, with no travel required.`,
  },
  {
    q: `What information is included in an FCA report for ${city} participants?`,
    a: `Every Functional Capacity Assessment report includes a functional baseline across the six NDIS functional capacity domains and broader functional areas, a daily-functioning analysis, supervision and safety profile, support recommendations, and goals aligned with the participant's NDIS plan — formatted to the NDIA's reasonable-and-necessary framework.`,
  },
  {
    q: `How do Support Coordinators in ${city} refer a participant?`,
    a: `Support Coordinators in ${city} can refer NDIS participants through the online referral form or by emailing the team directly. Referrals are acknowledged within one business day and a dedicated Coordinator pathway is offered with weekly status updates.`,
  },
];
